import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appRoutes, buildAiToolEditPath } from '../../app-routes';
import { PageHeader } from '../../components/common/PageHeader';
import { partnerApi, type ApiError, type ToolDefinition, type ToolDefinitionInput } from '../../lib/api';
import { ToolForm } from './ToolForm';
import { BLANK_TOOL_DRAFT } from './tool-inputs';
import { ToolTestPanel } from './ToolTestPanel';

export const ToolEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';

  const [draft, setDraft] = useState<ToolDefinitionInput>(BLANK_TOOL_DRAFT);
  const [seededId, setSeededId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(true);

  const existing = useQuery({
    queryKey: ['ai-tools', id],
    queryFn: () => partnerApi.getTool(id as string),
    enabled: !isNew && Boolean(id),
  });

  // Seed the draft from server data exactly once per tool id; refetchOnWindowFocus
  // would otherwise clobber in-progress edits.
  if (!isNew && existing.data && seededId !== id) {
    setSeededId(id ?? null);
    setDraft(toDraft(existing.data));
  }

  const createMutation = useMutation({
    mutationFn: (input: ToolDefinitionInput) => partnerApi.createTool(input),
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['ai-tools'] });
      navigate(buildAiToolEditPath(created.id));
      setSaveError(null);
    },
    onError: (err: ApiError) => setSaveError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<ToolDefinitionInput>) => partnerApi.updateTool(id as string, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-tools'] });
      queryClient.invalidateQueries({ queryKey: ['ai-tools', id] });
      setSaveError(null);
    },
    onError: (err: ApiError) => setSaveError(err.message),
  });

  const handleSave = () => {
    setSaveError(null);
    if (isNew) {
      createMutation.mutate(draft);
    } else {
      updateMutation.mutate(draft);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const tool = existing.data;

  const headerTitle = useMemo(() => {
    if (isNew) return 'New tool';
    if (tool?.name) return tool.name;
    return 'Edit tool';
  }, [isNew, tool?.name]);

  return (
    <div className="container mx-auto p-6">
      <PageHeader
        title={headerTitle}
        description={
          isNew
            ? 'Configure an HTTP tool that an AI agent can call on your behalf.'
            : (tool?.description ?? '')
        }
        right={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(appRoutes.AI_TOOLS)}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted">
              Back
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !isFormValid}
              title={!isFormValid ? 'Fix invalid JSON before saving' : undefined}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {isSaving ? 'Saving…' : isNew ? 'Create tool' : 'Save'}
            </button>
          </div>
        }
      />

      {saveError && (
        <div className="mt-4 whitespace-pre-wrap rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {saveError}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 rounded-lg border bg-white p-4">
          <ToolForm
            draft={draft}
            onChange={setDraft}
            isEditing={!isNew}
            onValidityChange={setIsFormValid}
            canonicalName={tool?.name}
          />
        </div>

        {!isNew && tool && (
          <aside className="min-w-0 lg:sticky lg:top-6 lg:self-start">
            <ToolTestPanel tool={tool} />
          </aside>
        )}
      </div>
    </div>
  );
};

function toDraft(tool: ToolDefinition): ToolDefinitionInput {
  return {
    display_name: tool.display_name,
    description: tool.description,
    http_method: tool.http_method,
    base_url: tool.base_url,
    path_template: tool.path_template,
    auth_type: tool.auth_type,
    auth_config: tool.auth_config,
    credentials: tool.credentials,
    request_schema: tool.request_schema,
    request_mapping: tool.request_mapping,
    response_mapping: tool.response_mapping,
    schema_mapping_refs: tool.schema_mapping_refs,
    required_scope: tool.required_scope,
    cacheable: tool.cacheable,
    cache_ttl_ms: tool.cache_ttl_ms,
    claim_enforcement: tool.claim_enforcement,
    timeout_ms: tool.timeout_ms,
    retry_policy: tool.retry_policy,
    annotations: tool.annotations,
    variables: tool.variables,
    status: tool.status,
  };
}
